// API клиент для работы с backend
const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:3001/api' : 'https://api.letstravel.ee/api');

class AdminAPI {
  constructor() {
    this.token = localStorage.getItem('admin_token');
  }

  getAuthHeaders() {
    return {
      'Content-Type': 'application/json',
      ...(this.token && { 'Authorization': `Bearer ${this.token}` })
    };
  }

  handleAuthError(response) {
    if (response.status === 401 || response.status === 403) {
      this.logout();
      window.dispatchEvent(new CustomEvent('admin:unauthorized'));
    }
  }

  // ==================== AUTH ====================
  
  async login(username, password) {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка входа');
    }
    
    const data = await response.json();
    this.token = data.token;
    localStorage.setItem('admin_token', data.token);
    return data;
  }

  logout() {
    this.token = null;
    localStorage.removeItem('admin_token');
  }

  isAuthenticated() {
    return !!this.token;
  }

  // ==================== DESTINATIONS ====================
  
  async getDestinations() {
    const response = await fetch(`${API_URL}/destinations`);
    if (!response.ok) throw new Error('Ошибка загрузки направлений');
    return response.json();
  }

  async createDestination(data) {
    const response = await fetch(`${API_URL}/destinations`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка создания направления');
    }
    
    return response.json();
  }

  async updateDestination(id, data) {
    const response = await fetch(`${API_URL}/destinations/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка обновления направления');
    }
    
    return response.json();
  }

  async deleteDestination(id) {
    const response = await fetch(`${API_URL}/destinations/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка удаления направления');
    }
    
    return response.json();
  }

  // ==================== PACKAGES ====================
  
  async getPackages() {
    const response = await fetch(`${API_URL}/packages`);
    if (!response.ok) throw new Error('Ошибка загрузки пакетов');
    return response.json();
  }

  async createPackage(data) {
    const response = await fetch(`${API_URL}/packages`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка создания пакета');
    }
    
    return response.json();
  }

  async updatePackage(id, data) {
    const response = await fetch(`${API_URL}/packages/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка обновления пакета');
    }
    
    return response.json();
  }

  async deletePackage(id) {
    const response = await fetch(`${API_URL}/packages/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка удаления пакета');
    }
    
    return response.json();
  }

  // ==================== BOOKINGS ====================
  
  async getBookings() {
    const response = await fetch(`${API_URL}/bookings`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) throw new Error('Ошибка загрузки заявок');
    return response.json();
  }

  async createBooking(data) {
    const response = await fetch(`${API_URL}/bookings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка создания заявки');
    }
    
    return response.json();
  }

  async updateBookingStatus(id, status) {
    const response = await fetch(`${API_URL}/bookings/${id}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ status })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка обновления статуса');
    }
    
    return response.json();
  }

  // ==================== UPLOAD ====================
  
  async uploadImage(file) {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await fetch(`${API_URL}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`
      },
      body: formData
    });
    
    if (!response.ok) {
      this.handleAuthError(response);
      const error = await response.json();
      throw new Error(error.error || 'Ошибка загрузки изображения');
    }
    
    return response.json();
  }

  async uploadMultipleImages(files) {
    const formData = new FormData();
    files.forEach(file => formData.append('images', file));
    
    const response = await fetch(`${API_URL}/upload/multiple`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`
      },
      body: formData
    });
    
    if (!response.ok) {
      this.handleAuthError(response);
      const error = await response.json();
      throw new Error(error.error || 'Ошибка загрузки изображений');
    }
    
    return response.json();
  }

  // ==================== UPLOADS LIST ====================

  async getUploadedImages() {
    const response = await fetch(`${API_URL}/uploads`, {
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      this.handleAuthError(response);
      throw new Error('Ошибка загрузки списка изображений');
    }
    return response.json();
  }

  async deleteImage(filename) {
    const response = await fetch(`${API_URL}/uploads/${filename}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      this.handleAuthError(response);
      const error = await response.json();
      throw new Error(error.error || 'Ошибка удаления');
    }
    return response.json();
  }

  // ==================== POSTS ====================

  async getPosts() {
    const response = await fetch(`${API_URL}/posts`);
    if (!response.ok) throw new Error('Ошибка загрузки постов');
    return response.json();
  }

  async createPost(data) {
    const response = await fetch(`${API_URL}/posts`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка создания поста');
    }
    return response.json();
  }

  async updatePost(id, data) {
    const response = await fetch(`${API_URL}/posts/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка обновления поста');
    }
    return response.json();
  }

  async deletePost(id) {
    const response = await fetch(`${API_URL}/posts/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка удаления поста');
    }
    return response.json();
  }

  // ==================== HOTELS ====================

  async getHotels() {
    const response = await fetch(`${API_URL}/hotels`);
    if (!response.ok) throw new Error('Ошибка загрузки отелей');
    return response.json();
  }

  async createHotel(data) {
    const response = await fetch(`${API_URL}/hotels`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const e = await response.json();
      throw new Error(e.error || 'Ошибка создания отеля');
    }
    return response.json();
  }

  async updateHotel(id, data) {
    const response = await fetch(`${API_URL}/hotels/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const e = await response.json();
      throw new Error(e.error || 'Ошибка обновления отеля');
    }
    return response.json();
  }

  async deleteHotel(id) {
    const response = await fetch(`${API_URL}/hotels/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      const e = await response.json();
      throw new Error(e.error || 'Ошибка удаления отеля');
    }
    return response.json();
  }

  // ==================== STATS ====================
  
  async getStats() {
    const response = await fetch(`${API_URL}/stats`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      this.handleAuthError(response);
      throw new Error('Ошибка загрузки статистики');
    }
    return response.json();
  }
}

export default new AdminAPI();
